import { CSSProperties, FC, ReactNode, useMemo, useState } from 'react';
import { Dialog } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { Tab } from '../../components/Tab';
import { Typography } from '../../components/Typography';
import { CloseRounded } from '../../components/svgs';
import { QuickGuideContent } from '../QuickGuideModal/QuickGuideContent';
import { ContentData, DEFAULT_CONTENT, ScoringContent, TopTab } from '../QuickGuideModal/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type HowToScoreTabId = 'all-players' | 'gk-def' | 'bonus-pts';

export interface HowToScoreCardProps {
  title?: string;
  closeLabel?: string;

  selectedTab?: HowToScoreTabId;
  defaultSelectedTab?: HowToScoreTabId;
  onSelectedTabChange?: (tab: HowToScoreTabId) => void;

  onClose?: () => void;

  /**
   * Optional scoring override per tab.
   * Useful for localisation or bespoke scoring rules.
   */
  contentByTab?: Partial<Record<HowToScoreTabId, ScoringContent>>;
  className?: string;

  /**
   * When true, hides the player selection tabs (ALL PLAYERS, GK & DEF, BONUS PTS)
   * and only shows the first tab content without tab navigation.
   */
  hidePlayerTabs?: boolean;
}

export interface HowToScoreModalProps extends Omit<HowToScoreCardProps, 'className' | 'onClose'> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  className?: string;
  /**
   * When true, hides the player selection tabs (ALL PLAYERS, GK & DEF, BONUS PTS)
   * and only shows the first tab content without tab navigation.
   */
  hidePlayerTabs?: boolean;
}

const HOW_TO_SCORE_TABS: { id: HowToScoreTabId; label: string }[] = [
  { id: 'all-players', label: 'ALL PLAYERS' },
  { id: 'gk-def', label: 'GK & DEF' },
  { id: 'bonus-pts', label: 'BONUS PTS' },
];

// Tab tokens are usually provided via `.components`. Since Dialog content is
// portaled to document.body, we attach the needed tokens locally.
const TAB_THEME_VARS: CSSProperties = {
  ['--tab-bg-selected' as string]: '#030033',
  ['--tab-bg-disabled' as string]: '#eae9fd',
  ['--tab-bg-hover' as string]: '#f8f8ff',
  ['--tab-border-default' as string]: '#eae9fd',
  ['--tab-border-disabled' as string]: '#eae9fd',
  ['--tab-border-selected-filled' as string]: '#030033',
  ['--tab-label-default' as string]: '#222526',
  ['--tab-label-selected' as string]: '#ffffff',
  ['--tab-label-disabled' as string]: '#9ca3af',
};

const DEFAULT_HOW_TO_SCORE_CONTENT: Record<HowToScoreTabId, ScoringContent> = {
  'all-players': {
    ...(DEFAULT_CONTENT.points['all-players'] as ScoringContent),
    showLearnMore: false,
  },
  'gk-def': {
    ...(DEFAULT_CONTENT.points['gk-def'] as ScoringContent),
    showLearnMore: false,
  },
  'bonus-pts': DEFAULT_CONTENT.points['bonus-pts'] as ScoringContent,
};

// ─── Card (pure UI) ──────────────────────────────────────────────────────────

export const HowToScoreCard: FC<HowToScoreCardProps> = ({
  title = 'HOW TO SCORE',
  closeLabel = 'Close',
  selectedTab,
  defaultSelectedTab = 'all-players',
  onSelectedTabChange,
  onClose,
  contentByTab,
  className,
  hidePlayerTabs = false,
}) => {
  const [internalSelectedTab, setInternalSelectedTab] =
    useState<HowToScoreTabId>(defaultSelectedTab);

  const activeTab = selectedTab ?? internalSelectedTab;
  const isBonusTab = activeTab === 'bonus-pts';

  const mergedPointsContent = useMemo<Record<HowToScoreTabId, ScoringContent>>(
    () => ({
      'all-players': contentByTab?.['all-players'] ?? DEFAULT_HOW_TO_SCORE_CONTENT['all-players'],
      'gk-def': contentByTab?.['gk-def'] ?? DEFAULT_HOW_TO_SCORE_CONTENT['gk-def'],
      'bonus-pts': contentByTab?.['bonus-pts'] ?? DEFAULT_HOW_TO_SCORE_CONTENT['bonus-pts'],
    }),
    [contentByTab]
  );

  const contentOverride = useMemo<Record<TopTab, Record<string, ContentData>>>(
    () => ({
      ...DEFAULT_CONTENT,
      points: {
        ...DEFAULT_CONTENT.points,
        ...mergedPointsContent,
      },
    }),
    [mergedPointsContent]
  );

  const handleTabChange = (nextTab: HowToScoreTabId) => {
    if (selectedTab === undefined) {
      setInternalSelectedTab(nextTab);
    }
    onSelectedTabChange?.(nextTab);
  };

  return (
    <div
      className={twMerge(
        'flex max-h-[calc(100dvh-32px)] w-full flex-col overflow-hidden rounded-[12px] bg-white',
        'shadow-[0px_4px_40px_0px_rgba(34,37,38,0.2)]',
        className
      )}
    >
      <div className="flex max-h-[68px] items-center justify-between border-b border-[#eae9fd] px-4 py-5">
        <Typography as="h2" variant="label050" className="text-[#222526] !font-[800]">
          {title}
        </Typography>

        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 rounded-[4px] text-[#222526] hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
          aria-label={closeLabel}
        >
          <Typography as="span" variant="button020" className="text-[#222526]">
            {closeLabel}
          </Typography>
          <span className="inline-flex size-6 items-center justify-center">
            <CloseRounded className="size-4 text-[#222526]" aria-hidden />
          </span>
        </button>
      </div>

      <div
        className={twMerge(
          'flex min-h-0 flex-col gap-4 overflow-y-auto px-4 pt-4',
          isBonusTab ? 'pb-4' : 'pb-1'
        )}
      >
        <div className="flex flex-col gap-1 w-full">
          {!hidePlayerTabs && (
            <div style={TAB_THEME_VARS} className="w-full">
              <div className="flex w-full items-center rounded-full border-2 border-[#030033] p-0">
                {HOW_TO_SCORE_TABS.map(tab => (
                  <Tab
                    key={tab.id}
                    type="button"
                    selected={activeTab === tab.id}
                    variant="filled"
                    appearance="pill"
                    labelVariant="label020"
                    onClick={() => handleTabChange(tab.id)}
                    className={twMerge(
                      'flex h-12 flex-1 items-center justify-center !gap-0 !border-0 !px-3 !py-3'
                    )}
                  >
                    {tab.label}
                  </Tab>
                ))}
              </div>
            </div>
          )}

          <QuickGuideContent topTab="points" subTab={activeTab} contentOverride={contentOverride} />
        </div>
      </div>
    </div>
  );
};

// ─── Dialog wrapper ───────────────────────────────────────────────────────────

export const HowToScoreModal: FC<HowToScoreModalProps> = ({
  open,
  onOpenChange,
  trigger,
  className,
  ...cardProps
}) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}

    <Dialog.Portal>
      <Dialog.Overlay
        className={twMerge(
          'fixed inset-0 z-50 bg-black/50',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
        )}
      />

      <Dialog.Content
        className={twMerge(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-[411px] max-w-[calc(100vw-32px)]',
          'focus:outline-none',
          className
        )}
        aria-describedby={undefined}
      >
        <Dialog.Title className="sr-only">HOW TO SCORE</Dialog.Title>
        <HowToScoreCard {...cardProps} onClose={() => onOpenChange?.(false)} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);
